import {useCallback, useEffect, useMemo, useState} from 'react';
import ClayAlert from '@clayui/alert';
import ClayLoadingIndicator from '@clayui/loading-indicator';
import {fetchMarketplaces} from './api/MarketplaceApi';
import {ConnectedSuppliersPanel} from './components/ConnectedSuppliersPanel';
import {MarketplaceTable} from './components/MarketplaceTable';
import {useTranslation} from './context/TranslationContext';
import {Marketplace} from './types';
import './styles/main.scss';

const App = () => {
    const {t} = useTranslation();
    const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadMarketplaces = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setMarketplaces(await fetchMarketplaces());
        } catch (loadError) {
            const message = loadError instanceof Error ? loadError.message : '';
            setError(message || t('an-unexpected-error-occurred'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadMarketplaces();
    }, [loadMarketplaces]);

    const selectedMarketplace = useMemo(
        () => marketplaces.find((marketplace) => marketplace.id === selectedMarketplaceId) ?? null,
        [marketplaces, selectedMarketplaceId]
    );

    return (
        <div className="apx-marketplaces">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0">{t('apx-marketplaces-title')}</h2>
            </div>

            {error && (
                <ClayAlert
                    displayType="danger"
                    onClose={() => setError('')}
                    title={t('error')}
                >
                    {error}
                </ClayAlert>
            )}

            {loading ? (
                <ClayLoadingIndicator displayType="secondary" size="md" />
            ) : (
                <>
                    <MarketplaceTable
                        marketplaces={marketplaces}
                        onSelect={setSelectedMarketplaceId}
                        selectedMarketplaceId={selectedMarketplaceId}
                    />
                    {selectedMarketplace && (
                        <ConnectedSuppliersPanel marketplace={selectedMarketplace} />
                    )}
                </>
            )}
        </div>
    );
};

export default App;
